/** @file The overlay sink: output chunks, allocator, inverse camera and the white/font pages. */
import { affine } from '@pose2d/math/affine';
import type { RenderBuffers } from '@pose2d/geometry/types';
import type { Affine } from '@pose2d/math/types';
import type { FontAtlas } from '../font/fontAtlas';
import type { OverlayInput } from '../overlayInput';

/** Mutable state of one buildOverlay call; primitives are given in pixels (y down) and stored in skeleton space. */
export interface Sink {
  out: RenderBuffers[];
  allocate: () => RenderBuffers;
  /** Chunks touched so far; the current chunk is `out[used - 1]` (none while 0). */
  used: number;
  /** Pixel to skeleton space: the inverse of `camera.worldToPixel`. */
  toWorld: Affine;
  white: number;
  font: number;
  atlas: FontAtlas;
  /** Viewport size in pixels (labels and grids clip to it). */
  width: number;
  height: number;
  /** Labels a layer counted as dropped by the per-layer cap without queuing them (vertex ids past 2,000). */
  droppedLabels: number;
}

/** Starts a sink over `out` (reused from index 0, extended through `input.allocate`). */
export function createSink(out: RenderBuffers[], input: Pick<OverlayInput, 'camera' | 'textures' | 'font' | 'allocate'>): Sink {
  return {
    out,
    allocate: input.allocate,
    used: 0,
    toWorld: affine.invert(new Float64Array(6), input.camera.worldToPixel),
    white: input.textures.white,
    font: input.textures.font,
    atlas: input.font,
    width: input.camera.width,
    height: input.camera.height,
    droppedLabels: 0,
  };
}
